import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { Row, Col, Form, Button, ButtonGroup } from "react-bootstrap";

import { useSelectMem } from "../../hooks/useMemoryDB";
import SelectMeta from "../selects/selectMeta";
import ErrorMessage from "../errorMessage";
import {
  DISTRIBUTIONS,
  collapsibleFields,
  distributionOf,
  fewValuesWarning,
  withCollapseDistribution,
  withCollapseField,
  withCollapsePerGroup
} from "./collapse";

const realms = ["content"];
const selectStyle = { width: "400px" };
const perValueStyle = { width: "90px" };

// The body of the Diversify box (general.collapse): a field turns it on, the empty choice turns it
// off (the key removed). The field list offers only the fields OpenSearch can collapse on. With a
// field: Items per value (perGroup) and, above 1, the Order of the items (distribution).
export default function DiversifySettings() {
  const { values, setValues } = useFormikContext();
  const collapse = values?.general?.collapse;
  const catalogs = useMemo(() => [values.contentType], [values.contentType]);

  const onField = useCallback(
    (field) => setValues(withCollapseField(values, field)),
    [setValues, values]
  );

  // The number as typed (0 or 1.5 reach the validation), an emptied input as "": a draft that keeps
  // the order, not 1, which would remove it while the user retypes the number
  const onPerGroup = useCallback(
    (e) => {
      const raw = e.target.value;
      setValues(withCollapsePerGroup(values, raw === "" ? "" : Number(raw)));
    },
    [setValues, values]
  );

  const onDistribution = useCallback(
    (distribution) => setValues(withCollapseDistribution(values, distribution)),
    [setValues, values]
  );

  // The catalog row of the chosen field read from the index (it has the values count)
  const field = collapse?.field;
  const fieldRows = useSelectMem(
    "metadata",
    useCallback(
      (m) =>
        Boolean(field) &&
        m.catalog === values.contentType &&
        m.id === field &&
        typeof m.valuesLength === "number",
      [field, values.contentType]
    )
  );
  const warning = useMemo(() => {
    const row = Array.isArray(fieldRows) ? fieldRows[0] : fieldRows;
    return field ? fewValuesWarning(row, collapse?.perGroup) : undefined;
  }, [collapse?.perGroup, field, fieldRows]);

  const perGroup = collapse?.perGroup ?? 1;
  const order = distributionOf(collapse);

  return (
    <div className="query-diversify">
      <Row>
        <Col sm="2">By</Col>
        <Col sm="3">
          <SelectMeta
            name="general.collapse.field"
            value={field || ""}
            catalogs={catalogs}
            realms={realms}
            excludeNested={true}
            filtro={collapsibleFields}
            style={selectStyle}
            errored={Boolean(collapse) && !field}
            handleChange={onField}
          />
          <ErrorMessage name="collapse" />
          {warning && <div className="query-diversify-warning">{warning}</div>}
          <div className="query-diversify-help">
            Keeps the best items for each value of the field, ranked by Order By: the block never
            shows more items with the same value than Items per value. Only keyword or numeric
            fields with one value per document are listed; a field missing from the list may need a
            metadata refresh.
          </div>
        </Col>
      </Row>
      {field && (
        <Row className="query-diversify-row">
          <Col sm="2">Items per value</Col>
          <Col sm="8" className="query-diversify-per-value">
            <Form.Control
              type="number"
              min={1}
              step={1}
              size="sm"
              name="general.collapse.perGroup"
              value={perGroup}
              style={perValueStyle}
              isInvalid={!(Number.isInteger(perGroup) && perGroup >= 1)}
              onChange={onPerGroup}
            />
            <div className="query-diversify-per-value-help">
              Above 1, pages work only with Paginate From Cache in the endpoint.
            </div>
          </Col>
        </Row>
      )}
      {field && perGroup > 1 && (
        <Row className="query-diversify-row query-diversify-order">
          <Col sm="2">Order</Col>
          <Col sm="8">
            <ButtonGroup size="sm" aria-label="Order">
              {DISTRIBUTIONS.map((d) => (
                <Button
                  key={d.value}
                  title={d.title}
                  variant={d.value === order.value ? "primary" : "outline-secondary"}
                  aria-pressed={d.value === order.value}
                  onClick={() => onDistribution(d.value)}
                >
                  {d.label}
                </Button>
              ))}
            </ButtonGroup>
            <div className="query-diversify-example" title={order.title}>
              {order.example.split(" ").map((item, i) => (
                <span key={item} className={`query-diversify-item group-${item[0]}`}>
                  {i > 0 && " "}
                  {item}
                </span>
              ))}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}
